import {
  Box,
  Text,
  Button,
  Link,
  Grid,
  GridItem,
  Flex,
} from '@everlywell/ui-kit';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import useCart from 'components/Cart/Cart.hooks';
import StripePayment, {
  StripePaymentProps,
} from 'components/Stripe/StripePayment';
import StripeMerchandisingWidget from 'components/StripeMerchandisingWidget';
import { useToastContext } from 'components/ToastContext/ToastContext';
import { Link as GatsbyLink, navigate } from 'gatsby';
import React, { useEffect, useState } from 'react';
import analytics, { skusForMixpanel } from 'utils/analytics';
import { processExpressPayOrder } from 'utils/api/cartApis';
import { currentOrderGrandTotalInCents } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT_BASE_PATH } from 'utils/constants/checkout';
import { VITAMINS } from 'utils/constants/products';
import { logError } from 'utils/helpers';
import {
  hasLabVisitItemInCart,
  hasSubscriptionItemInCart,
  stripePublicKey,
} from 'utils/helpers/paymentHelpers';

export type CartFooterProps = {};

/**
 * Cart Footer Component.
 */
function CartFooter(props: CartFooterProps): JSX.Element {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  const [hasStripeError, setHasStripeError] = useState(false);

  const { order, resetOrder } = useCart();

  // ---REMOVE hasLabVisitItemInCart LOGIC WHEN EXPRESS PAY IN BDC CARTS IS FIXED ---
  // check if there are any vitamins in the cart
  // do not show Stripe Merchandising widget if there are vitamins in the cart

  const lineItems = order?.line_items || [];

  const showAltPayments =
    !hasSubscriptionItemInCart(lineItems) && !hasLabVisitItemInCart(lineItems);

  const hasVitaminItemInCart = lineItems.some((item) =>
    item.product_sku ? VITAMINS.includes(item.product_sku) : false,
  );

  const showStripeMerchandisingWidget = !hasVitaminItemInCart;

  useEffect(() => {
    if (!stripePromise) {
      setStripePromise(loadStripe(stripePublicKey));
    }
  }, [stripePromise]);

  const currentOrder = order;

  const variantsSkus = skusForMixpanel(currentOrder?.line_items);

  const shipping = parseFloat(currentOrder?.shipping || '');
  const cartTotalPrice = currentOrder ? +currentOrder.total : 0;
  const totalPrice = cartTotalPrice - shipping;

  const handleCheckoutButtonClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Checkout Button',
      },
    });
  };

  // ### Express Pay
  // express pay total is in cents
  const expressPayTotal = currentOrderGrandTotalInCents(totalPrice);

  const toast = useToastContext();

  /* unable to test stripe in our unit tests at the moment */
  /* istanbul ignore next */
  const handleExpressPayPaymentConfirm: StripePaymentProps['handlePaymentConfirm'] =
    async (data, token) => {
      try {
        if (order) {
          const response = await processExpressPayOrder({
            type: 'entireOrder',
            order: order,
            data,
            token,
          });
          resetOrder();
          navigate(
            `/confirmation?orderId=${response.data.id}&registration_token=${response.data.registration_token}&referral=true`,
          );
        }
      } catch (e: any) {
        const error = e as unknown as Error;
        let errorMessage =
          'Oops! Something went wrong. Please try again or contact support for help';
        if (!/(network|syntax)/i.test(error.message)) {
          errorMessage = error.message;
        }
        toast.showToast({
          message: errorMessage,
          status: 'error',
        });
        logError('Error processing express pay payment', {
          component: 'CartFooter',
          method: 'handleExpressPayPaymentConfirm',
          experience: 'Cart',
        });
      }
    };

  return (
    <Box
      display="flex"
      background="tints.lightCream"
      borderTopRadius={['24px', '0']}
      boxShadow={['0px 4px 35px 0 rgba(0, 0, 0, 0.4)', 'none']}
      position="sticky"
      bottom="0"
      flexDirection="column"
      alignItems="center"
      padding={[4, 8]}
      paddingTop={[4, 3]}
    >
      <Grid spacing={3} alignEdges>
        {/* Checkout button is full width if stripe is not available */}
        <GridItem width={showAltPayments && !hasStripeError ? [1 / 2] : [1]}>
          <Button
            variant="primary"
            size="wide"
            colorScheme="greenCS"
            as={GatsbyLink}
            to={CHECKOUT_BASE_PATH}
            height={'48px'}
            onClick={handleCheckoutButtonClick}
          >
            Checkout
          </Button>
        </GridItem>

        {showAltPayments && !hasStripeError && (
          <GridItem
            width={[1 / 2]}
            sx={{ '.stripe-payment-new-cart': { marginTop: 0 } }}
          >
            <Elements stripe={stripePromise}>
              <StripePayment
                className="stripe-payment-new-cart"
                cartTotal={expressPayTotal}
                label={ANALYTICS.LABELS.EXPRESS_PAY_CART}
                variantsSkus={variantsSkus}
                handlePaymentConfirm={handleExpressPayPaymentConfirm}
                onStripeLoadingError={setHasStripeError}
                withApplePayWidget={false}
              />
            </Elements>
          </GridItem>
        )}
        {showStripeMerchandisingWidget ? (
          <GridItem width={[1]}>
            <Flex marginTop={2} justifyContent="center">
              <StripeMerchandisingWidget amount={expressPayTotal} />
            </Flex>
          </GridItem>
        ) : null}

        <GridItem width={[1]}>
          <Text textAlign={'center'} marginTop={3}>
            By completing this purchase you agree to our{' '}
            <Link
              size="lg"
              isExternal
              href="/terms-of-use/"
              sx={{ '&:hover:, &:focus': { textDecoration: 'underline' } }}
            >
              Terms of Service
            </Link>
          </Text>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default CartFooter;
